import { FormEvent, useEffect, useState } from "react";
import { Edit, Plus, RefreshCw, Save, Tag, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "./AdminLayout";
import {
  AdminShippingMethod,
  AdminShippingRule,
  AdminShippingRulePayload,
  AdminUser,
  deleteAdminShippingRule,
  getCurrentAdmin,
  listAdminShippingMethods,
  listAdminShippingRules,
  logout,
  saveAdminShippingRule,
} from "./api";
import { useConfirmDialog } from "./components/ConfirmDialog";
import { AdminSwitchCard } from "./components/AdminChoiceControl";

type FormState = {
  Name: string;
  ShippingMethodId: string;
  MinOrderAmount: string;
  FeeAmount: string;
  Priority: string;
  StartAt: string;
  EndAt: string;
  IsActive: boolean;
};

const emptyForm: FormState = {
  Name: "",
  ShippingMethodId: "",
  MinOrderAmount: "",
  FeeAmount: "0",
  Priority: "100",
  StartAt: "",
  EndAt: "",
  IsActive: true,
};

function toDatetimeLocal(value: string | null): string {
  if (!value) return "";
  // 後端回傳 ISO 字串，這裡截到分鐘給 <input type="datetime-local"> 使用。
  return value.slice(0, 16);
}

function itemToForm(item: AdminShippingRule): FormState {
  return {
    Name: item.Name,
    ShippingMethodId: item.ShippingMethodId || "",
    MinOrderAmount: item.MinOrderAmount !== null ? String(item.MinOrderAmount) : "",
    FeeAmount: String(item.FeeAmount),
    Priority: String(item.Priority),
    StartAt: toDatetimeLocal(item.StartAt),
    EndAt: toDatetimeLocal(item.EndAt),
    IsActive: item.IsActive,
  };
}

function formatDateTime(value: string | null): string {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("zh-TW", { year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" });
}

const inputClass = "h-11 w-full rounded-md border border-[#cfd8d2] px-3 font-normal outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15";

export default function AdminShippingRules() {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState<AdminUser | null>(null);
  const [items, setItems] = useState<AdminShippingRule[]>([]);
  const [methods, setMethods] = useState<AdminShippingMethod[]>([]);
  const [loading, setLoading] = useState(true);
  const [pageMessage, setPageMessage] = useState("");
  const [editingItem, setEditingItem] = useState<AdminShippingRule | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [formOpen, setFormOpen] = useState(false);
  const [formMessage, setFormMessage] = useState("");
  const [saving, setSaving] = useState(false);
  const { confirm, ConfirmDialog } = useConfirmDialog();

  async function load() {
    setLoading(true);
    setPageMessage("");
    try {
      const me = await getCurrentAdmin();
      setCurrentUser(me.User);
      const [rulesData, methodsData] = await Promise.all([listAdminShippingRules(), listAdminShippingMethods()]);
      setItems(rulesData.Items);
      setMethods(methodsData.Items);
    } catch (error) {
      setPageMessage(error instanceof Error ? error.message : "運費規則資料載入失敗。");
      navigate("/admin-login", { replace: true });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleLogout() {
    await logout().catch(() => undefined);
    navigate("/admin-login", { replace: true });
  }

  function openCreateForm() {
    setEditingItem(null);
    setForm(emptyForm);
    setFormMessage("");
    setFormOpen(true);
  }

  function openEditForm(item: AdminShippingRule) {
    setEditingItem(item);
    setForm(itemToForm(item));
    setFormMessage("");
    setFormOpen(true);
  }

  function closeForm() {
    if (saving) return;
    setFormOpen(false);
    setEditingItem(null);
    setForm(emptyForm);
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!form.Name.trim()) { setFormMessage("請輸入活動規則名稱。"); return; }
    if (form.StartAt && form.EndAt && form.StartAt > form.EndAt) { setFormMessage("開始時間不可晚於結束時間。"); return; }

    const payload: AdminShippingRulePayload = {
      Name: form.Name.trim(),
      ShippingMethodId: form.ShippingMethodId || null,
      MinOrderAmount: form.MinOrderAmount.trim() !== "" ? Number(form.MinOrderAmount) : null,
      FeeAmount: Number(form.FeeAmount || 0),
      Priority: Number(form.Priority || 100),
      StartAt: form.StartAt || null,
      EndAt: form.EndAt || null,
      IsActive: form.IsActive,
    };

    setSaving(true);
    setFormMessage("");
    try {
      await saveAdminShippingRule(editingItem?.ShippingRuleId || null, payload);
      closeForm();
      await load();
    } catch (error) {
      setFormMessage(error instanceof Error ? error.message : "運費規則儲存失敗。");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(item: AdminShippingRule) {
    const confirmed = await confirm({
      title: "刪除這個運費活動規則？",
      description: (
        <>
          確定要刪除 <strong className="font-bold text-[#203d32]">{item.Name}</strong> 嗎？刪除後將立即停止套用，恢復為原本的基本運費。
        </>
      ),
      confirmText: "刪除規則",
      cancelText: "取消",
      tone: "danger",
    });
    if (!confirmed) return;

    try {
      await deleteAdminShippingRule(item.ShippingRuleId);
      await load();
    } catch (error) {
      setPageMessage(error instanceof Error ? error.message : "運費規則刪除失敗。");
    }
  }

  const methodOptions = [{ value: "", label: "全部配送方式" }, ...methods.map((method) => ({ value: method.ShippingMethodId, label: method.Name }))];

  return (
    <AdminLayout sectionLabel="網站設定" title="運費活動規則" user={currentUser} onLogout={handleLogout}>
      <p className="mb-4 text-xs text-[#7b877f]">
        用於檔期活動（例如雙十一全站免運、滿千折運費等），可設定生效區間與適用的配送方式。同時符合多筆規則時，以「優先序」數字較小者為準；
        規則命中時會直接覆蓋該配送方式的運費金額（不是取比較低者），未命中任何規則則維持配送方式原本的基本運費。
      </p>

      <div className="mb-4 flex justify-end gap-2">
        <button className="inline-flex h-11 items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-5 font-bold text-[#315746] transition hover:bg-[#f0f5f2]" onClick={() => load()} type="button">
          <RefreshCw size={18} />
          重新整理
        </button>
        <button className="inline-flex h-11 items-center justify-center gap-2 rounded-md bg-[#315746] px-5 font-bold text-white transition hover:bg-[#203d32]" onClick={openCreateForm} type="button">
          <Plus size={20} />
          新增活動規則
        </button>
      </div>

      {pageMessage && <div className="mb-4 rounded-md border border-[#f2b8b5] bg-[#fdebea] px-4 py-3 text-sm font-medium text-[#8a1f1f]">{pageMessage}</div>}

      <div className="rounded-md border border-[#dfe6e1] bg-white">
        <div className="overflow-auto">
          <table className="w-full min-w-[960px] table-fixed border-collapse text-left">
            <thead className="bg-[#f0f3f1] text-sm text-[#59665f] shadow-[inset_0_-1px_0_#dfe6e1]">
              <tr>
                <th className="w-[180px] px-4 py-3">規則名稱</th>
                <th className="w-[140px] px-4 py-3">適用配送方式</th>
                <th className="w-[110px] px-4 py-3">最低金額</th>
                <th className="w-[100px] px-4 py-3">套用運費</th>
                <th className="w-[80px] px-4 py-3">優先序</th>
                <th className="w-[220px] px-4 py-3">生效區間</th>
                <th className="w-[100px] px-4 py-3">狀態</th>
                <th className="w-[160px] px-4 py-3">操作</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td className="px-4 py-10 text-center text-[#59665f]" colSpan={8}>載入中...</td></tr>
              ) : items.length === 0 ? (
                <tr><td className="px-4 py-10 text-center text-[#59665f]" colSpan={8}>尚未設定任何運費活動規則。</td></tr>
              ) : items.map((item) => (
                <tr className="border-t border-[#edf0ee]" key={item.ShippingRuleId}>
                  <td className="px-4 py-4">
                    <div className="flex items-center gap-2">
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#fdf1e0] text-[#9a6a1f]"><Tag size={16} /></span>
                      <p className="truncate font-bold text-[#203d32]">{item.Name}</p>
                    </div>
                  </td>
                  <td className="px-4 py-4">{item.ShippingMethodName || "全部方式"}</td>
                  <td className="px-4 py-4">{item.MinOrderAmount !== null ? `滿 NT$ ${item.MinOrderAmount.toLocaleString()}` : "不限"}</td>
                  <td className="px-4 py-4">{item.FeeAmount > 0 ? `NT$ ${item.FeeAmount.toLocaleString()}` : "免運"}</td>
                  <td className="px-4 py-4">{item.Priority}</td>
                  <td className="px-4 py-4 text-sm">
                    {item.StartAt || item.EndAt ? (
                      <>
                        {formatDateTime(item.StartAt)}
                        <br />~ {formatDateTime(item.EndAt)}
                      </>
                    ) : (
                      "不限期間"
                    )}
                  </td>
                  <td className="px-4 py-4">
                    <span className={`inline-flex rounded-full px-3 py-1 text-xs font-bold ${item.IsActive ? "bg-[#e9f4ec] text-[#245438]" : "bg-[#eeeeee] text-[#59635d]"}`}>{item.IsActive ? "啟用" : "停用"}</span>
                  </td>
                  <td className="px-4 py-4">
                    <div className="grid gap-2">
                      <button className="inline-flex h-10 items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-3 font-bold text-[#315746] transition hover:bg-[#f0f5f2]" onClick={() => openEditForm(item)} type="button">
                        <Edit size={16} />
                        編輯
                      </button>
                      <button className="inline-flex h-10 items-center justify-center gap-2 rounded-md border border-[#f2b8b5] bg-white px-3 font-bold text-[#b3261e] transition hover:bg-[#fdebea]" onClick={() => handleDelete(item)} type="button">
                        刪除
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {formOpen && (
        <div className="fixed inset-0 z-[70] flex items-end justify-center bg-[#10251c]/40 px-0 py-0 sm:items-center sm:px-4 sm:py-6">
          <button aria-label="關閉視窗" className="absolute inset-0 cursor-default" onClick={closeForm} type="button" />
          <form className="relative flex max-h-[90vh] w-full max-w-[760px] flex-col overflow-hidden rounded-t-lg border border-[#dfe6e1] bg-white shadow-[0_22px_70px_rgba(32,61,50,.2)] sm:rounded-md" onSubmit={handleSubmit}>
            <div className="flex shrink-0 items-start justify-between gap-4 border-b border-[#edf0ee] px-5 py-4">
              <div>
                <h2 className="text-xl font-bold text-[#203d32]">{editingItem ? "編輯運費活動規則" : "新增運費活動規則"}</h2>
                <p className="mt-1 text-sm text-[#7b877f]">生效期間內，符合條件的訂單會直接套用此運費金額。</p>
              </div>
              <button className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-[#d8e0db] text-[#315746] transition hover:bg-[#f0f5f2]" onClick={closeForm} type="button">
                <X size={18} />
              </button>
            </div>

            <div className="grid gap-4 overflow-y-auto px-5 py-5">
              {formMessage && <div className="rounded-md border border-[#f2b8b5] bg-[#fdebea] px-4 py-3 text-sm font-medium text-[#8a1f1f]">{formMessage}</div>}

              <label className="grid gap-2 text-sm font-bold text-[#315746]">
                <span>規則名稱 <span className="text-[#c3423f]">*</span></span>
                <input className={inputClass} placeholder="例如 雙十一全站免運" value={form.Name} onChange={(event) => setForm((current) => ({ ...current, Name: event.target.value }))} />
              </label>

              <label className="grid gap-2 text-sm font-bold text-[#315746]">
                適用配送方式
                <select className={inputClass} value={form.ShippingMethodId} onChange={(event) => setForm((current) => ({ ...current, ShippingMethodId: event.target.value }))}>
                  {methodOptions.map((option) => (
                    <option key={option.value || "all"} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </label>

              <div className="grid gap-4 sm:grid-cols-2">
                <label className="grid gap-2 text-sm font-bold text-[#315746]">
                  <span>最低訂單金額（選填）</span>
                  <input className={inputClass} min={0} placeholder="留空表示不限金額" type="number" value={form.MinOrderAmount} onChange={(event) => setForm((current) => ({ ...current, MinOrderAmount: event.target.value }))} />
                </label>
                <label className="grid gap-2 text-sm font-bold text-[#315746]">
                  <span>套用運費（元）</span>
                  <input className={inputClass} min={0} type="number" value={form.FeeAmount} onChange={(event) => setForm((current) => ({ ...current, FeeAmount: event.target.value }))} />
                </label>
              </div>

              <label className="grid gap-2 text-sm font-bold text-[#315746]">
                優先序（數字越小越優先套用）
                <input className={inputClass} type="number" value={form.Priority} onChange={(event) => setForm((current) => ({ ...current, Priority: event.target.value }))} />
              </label>

              {/* 開始／結束時間改成上下堆疊、各自佔滿整列寬度：datetime-local 在不同瀏覽器/系統下實際需要的寬度
                  不太一致，硬擠在兩欄容易讓文字跟日曆圖示疊在一起，堆疊後不管視窗多寬都不會再擠壓。 */}
              <div className="grid gap-4">
                <label className="grid gap-2 text-sm font-bold text-[#315746]">
                  <span>開始時間（選填）</span>
                  <input className={inputClass} type="datetime-local" value={form.StartAt} onChange={(event) => setForm((current) => ({ ...current, StartAt: event.target.value }))} />
                </label>
                <label className="grid gap-2 text-sm font-bold text-[#315746]">
                  <span>結束時間（選填）</span>
                  <input className={inputClass} type="datetime-local" value={form.EndAt} onChange={(event) => setForm((current) => ({ ...current, EndAt: event.target.value }))} />
                </label>
              </div>
              <p className="-mt-2 text-xs text-[#9a8a7a]">開始／結束時間皆留空表示不限期間、立即生效直到手動停用。</p>

              <AdminSwitchCard checked={form.IsActive} description="停用後即使在生效期間內也不會套用。" label="啟用此規則" onChange={(IsActive) => setForm((current) => ({ ...current, IsActive }))} />
            </div>

            <div className="flex shrink-0 flex-col-reverse gap-3 border-t border-[#edf0ee] px-5 py-4 sm:flex-row sm:justify-end">
              <button className="h-10 rounded-md border border-[#cfd8d2] bg-white px-4 font-bold text-[#315746] transition hover:bg-[#f0f5f2]" onClick={closeForm} type="button">
                取消
              </button>
              <button className="inline-flex h-10 items-center justify-center gap-2 rounded-md bg-[#315746] px-5 font-bold text-white transition hover:bg-[#203d32] disabled:cursor-not-allowed disabled:opacity-60" disabled={saving} type="submit">
                <Save size={18} />
                {saving ? "儲存中" : "儲存規則"}
              </button>
            </div>
          </form>
        </div>
      )}

      <ConfirmDialog />
    </AdminLayout>
  );
}
